import { useState, type ReactNode } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';

import { ChangePasswordModal } from '@/features/auth/ChangePasswordModal';
import { ResetLinkModal } from '@/features/auth/ResetLinkModal';
import { useAuth } from '@/features/auth/AuthContext';

const FULL_NAV = [
  { to: '/', label: 'Dashboard', icon: '◐' },
  { to: '/transactions', label: 'Transações', icon: '↕' },
  { to: '/accounts', label: 'Contas', icon: '🏦' },
  { to: '/cards', label: 'Cartões', icon: '💳' },
  { to: '/recurrences', label: 'Recorrências', icon: '🔁' },
  { to: '/categories', label: 'Categorias', icon: '🏷' },
  { to: '/institutions', label: 'Instituições', icon: '🏢' },
  { to: '/help', label: 'Ajuda', icon: '❓' },
];

const BOTTOM_NAV = [
  { to: '/', label: 'Início', icon: '◐' },
  { to: '/transactions', label: 'Lanç.', icon: '↕' },
  { to: '/accounts', label: 'Contas', icon: '🏦' },
  { to: '/__more', label: 'Mais', icon: '☰' },
];

export function AppLayout({ children }: { children: ReactNode }) {
  const { me, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  async function afterPasswordChange() {
    setPwOpen(false);
    setNotice('senha trocada — faça login novamente.');
    setTimeout(async () => {
      await logout().catch(() => undefined);
      navigate('/login', { replace: true });
    }, 1500);
  }

  return (
    <div className="flex min-h-screen bg-slate-50 text-slate-900">
      {/* sidebar fixa em desktop */}
      <aside className="hidden w-56 shrink-0 border-r border-slate-200 bg-white p-4 lg:block">
        <SidebarContent />
      </aside>

      {/* drawer mobile */}
      {drawerOpen && (
        <div
          className="fixed inset-0 z-40 bg-slate-900/50 lg:hidden"
          onClick={() => setDrawerOpen(false)}
        >
          <aside
            className="safe-pt safe-pb h-full w-72 max-w-[85vw] bg-white p-4 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <SidebarContent onNavigate={() => setDrawerOpen(false)} />
          </aside>
        </div>
      )}

      <div className="flex flex-1 flex-col">
        {/* header sticky */}
        <header className="safe-pt sticky top-0 z-30 flex items-center justify-between gap-3 border-b border-slate-200 bg-white px-4 py-2 text-sm lg:px-6 lg:py-3">
          <button
            type="button"
            className="tap rounded-md p-2 lg:hidden"
            onClick={() => setDrawerOpen(true)}
            aria-label="abrir menu"
          >
            ☰
          </button>
          <div className="flex flex-1 flex-col leading-tight lg:hidden">
            <span className="text-sm font-semibold">cssystem</span>
            <span className="text-[11px] text-slate-500">{me?.household?.name}</span>
          </div>
          <div className="hidden flex-1 lg:block" />

          <div className="relative">
            <button
              type="button"
              onClick={() => setAccountMenuOpen((v) => !v)}
              className="tap rounded-md border border-slate-300 px-3 py-1 text-sm hover:bg-slate-100"
            >
              <span className="hidden sm:inline">{me?.user?.full_name} · </span>
              <span>{me?.role}</span>
              <span className="ml-1">▾</span>
            </button>
            {accountMenuOpen && (
              <div className="absolute right-0 top-full z-40 mt-1 w-48 rounded-md border border-slate-200 bg-white py-1 shadow-md">
                <button
                  type="button"
                  className="block w-full px-3 py-2 text-left text-sm hover:bg-slate-50"
                  onClick={() => {
                    setAccountMenuOpen(false);
                    setPwOpen(true);
                  }}
                >
                  trocar senha
                </button>
                {me?.role === 'owner' && (
                  <button
                    type="button"
                    className="block w-full px-3 py-2 text-left text-sm hover:bg-slate-50"
                    onClick={() => {
                      setAccountMenuOpen(false);
                      setResetOpen(true);
                    }}
                  >
                    redefinir senha de membro
                  </button>
                )}
                <button
                  type="button"
                  className="block w-full px-3 py-2 text-left text-sm hover:bg-slate-50"
                  onClick={() => logout()}
                >
                  sair
                </button>
              </div>
            )}
          </div>
        </header>

        {notice && (
          <div className="mx-4 mt-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm text-emerald-800 lg:mx-6 lg:mt-4">
            {notice}
          </div>
        )}

        {/* main: padding-bottom maior no mobile pra não ficar atrás da bottom nav */}
        <main className="flex-1 px-4 pb-24 pt-4 lg:px-6 lg:pb-6">{children}</main>

        {/* FAB mobile — atalho pra novo lançamento */}
        <Link
          to="/transactions?new=1"
          aria-label="novo lançamento"
          className="fixed bottom-20 right-4 z-30 flex h-14 w-14 items-center justify-center rounded-full bg-slate-900 text-2xl text-white shadow-lg lg:hidden"
        >
          +
        </Link>

        {/* bottom nav mobile */}
        <nav className="safe-pb fixed inset-x-0 bottom-0 z-20 flex border-t border-slate-200 bg-white lg:hidden">
          {BOTTOM_NAV.map((item) => {
            const active =
              item.to === location.pathname ||
              (item.to !== '/' && item.to !== '/__more' && location.pathname.startsWith(item.to));
            if (item.to === '/__more') {
              return (
                <button
                  type="button"
                  key={item.to}
                  onClick={() => setDrawerOpen(true)}
                  className="flex flex-1 flex-col items-center justify-center gap-0.5 py-2 text-[11px] text-slate-600"
                >
                  <span className="text-lg leading-none">{item.icon}</span>
                  <span>{item.label}</span>
                </button>
              );
            }
            return (
              <Link
                key={item.to}
                to={item.to}
                className={`flex flex-1 flex-col items-center justify-center gap-0.5 py-2 text-[11px] ${
                  active ? 'text-slate-900' : 'text-slate-500'
                }`}
              >
                <span className="text-lg leading-none">{item.icon}</span>
                <span>{item.label}</span>
              </Link>
            );
          })}
        </nav>

        <ChangePasswordModal
          open={pwOpen}
          onClose={() => setPwOpen(false)}
          onSuccess={afterPasswordChange}
        />
        <ResetLinkModal open={resetOpen} onClose={() => setResetOpen(false)} />
      </div>
    </div>
  );
}

function SidebarContent({ onNavigate }: { onNavigate?: () => void } = {}) {
  return (
    <>
      <div className="mb-6">
        <p className="text-lg font-semibold tracking-tight">cssystem</p>
      </div>
      <nav className="flex flex-col gap-1 text-sm">
        {FULL_NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === '/'}
            onClick={onNavigate}
            className={({ isActive }) =>
              `tap flex items-center gap-3 rounded-md px-3 py-2 ${
                isActive ? 'bg-slate-900 text-white' : 'text-slate-700 hover:bg-slate-100'
              }`
            }
          >
            <span className="text-base leading-none">{item.icon}</span>
            <span>{item.label}</span>
          </NavLink>
        ))}
      </nav>
    </>
  );
}
